import { useMemo } from "react";
import { parseISO } from "date-fns";
import { GraphQLFormattedError } from "graphql";
import { Card, Flex, FullscreenTakeover, Text, toast } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import { AddressType, TaxIdType, ProfileRelationshipEnum } from "~/graphql/supergraphEnums";
import {
  useCreateInstitutionProileMutation,
  useOverrideRiskRatingMutation,
  useUpdateInstitutionProfileMutation,
  useUpdatePicrPositionDetailsMutation,
} from "~/graphql/supergraphHooks";
import {
  type ComplianceInstitutionProfileInput,
  type RiskRating,
} from "~/graphql/supergraphTypes";
import RiskRatingRadioBar from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/RiskRatingRadioBar";
import { ProfileDetailsType } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/types";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import { shouldShowRiskRating } from "~/pages/Compliance/utils";
import {
  resolveAmlEntityId,
  resolveAmlEntityTypeNameFromId,
} from "~/pages/Compliance/utils/amlTypeIdUtil";
import { toISOFormat } from "~/utils/datetime";
import ProfileEntityFormFields from "../../../OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm/ProfileEntityFormFields";
import { isEntityClientOnboardingCase } from "../../../OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/utils/isEntityClientOnboardingCase";
import { getOwnershipPercentage, getProfileNameForProfileType } from "../../../utils";
import EntityFormFields from "../../EntityFields";
import {
  ACCOUNT_TYPE_TO_VIEWABLE_ACCOUNT,
  VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE,
} from "../../EntityFields/constants";
import { buildRelationshipTypes } from "../../ProfileActions/utils";
import { getSchema } from "./schema";
import * as styles from "./styles.css";
import { FormValues } from "./types";

const optionalIntField = (value?: string): number | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : undefined;
};

interface Props {
  caseId: number;
  complianceProfile: ProfileDetailsType | null | undefined;
  programId: number;
  positionId?: string | null;
  isBeneficiaryProfile?: boolean;
  saveButtonLabel?: string;
  onCancel?: () => void;
  onCompleted: () => void;
}

const EditEntityForm = ({
  caseId,
  complianceProfile,
  programId,
  positionId,
  isBeneficiaryProfile = false,
  saveButtonLabel = "Save and exit",
  onCancel,
  onCompleted,
}: Props) => {
  const { aiExtractionSupported, arenaAccessId, caseType } = useCurrentCaseContext();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const showRiskRating = shouldShowRiskRating(programId);
  const isInvestingProfileForCase =
    complianceProfile?.complianceCases.find((ccp) => ccp.complianceCase?.id === caseId)
      ?.isInvestingProfile ?? false;

  const showOnboardingBusinessFields =
    isEntityClientOnboardingCase(caseType) && isInvestingProfileForCase;

  const [createInstitutionProfile] = useCreateInstitutionProileMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
    refetchQueries: ["GetComplianceCase", "GetCaseInvestingProfileRiskRating", "GetPositions"],
    awaitRefetchQueries: true,
  });

  const [updateInstitutionProfile] = useUpdateInstitutionProfileMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
    refetchQueries: ["GetComplianceCase", "GetCaseInvestingProfileRiskRating", "GetPositions"],
    awaitRefetchQueries: true,
  });

  const [overrideRiskRating] = useOverrideRiskRatingMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
    refetchQueries: ["GetComplianceCase", "GetCaseInvestingProfileRiskRating", "GetPositions"],
    awaitRefetchQueries: true,
  });

  const [updatePicrPositionDetails] = useUpdatePicrPositionDetailsMutation({
    refetchQueries: ["GetPicrPositionDetails"],
    awaitRefetchQueries: true,
    fetchPolicy: "network-only",
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  const childProfile = complianceProfile;
  const entityName = complianceProfile && getProfileNameForProfileType(complianceProfile);

  const dateOfFormation = complianceProfile?.dateOfOrganization
    ? parseISO(complianceProfile?.dateOfOrganization)
    : undefined;

  const address = complianceProfile?.addresses.find((x) => x.isPrimaryDomicile);
  const stateOrProvince = address?.state ? address?.state?.code : address?.province;

  const isOwner = complianceProfile?.parentRelationships
    ?.map((pr) => pr.relationshipType)
    .includes(ProfileRelationshipEnum.Owner)
    ? true
    : false;

  const isController = complianceProfile?.parentRelationships
    ?.map((pr) => pr.relationshipType)
    .includes(ProfileRelationshipEnum.Controller)
    ? true
    : false;

  const childAddress = childProfile?.addresses.find((x) => x.isPrimaryDomicile);
  const needsOwnershipPercentage = !!complianceProfile?.parentRelationships.length;
  const initialValues: FormValues = {
    entityType: ACCOUNT_TYPE_TO_VIEWABLE_ACCOUNT[complianceProfile?.accountType ?? ""],
    amlEntityType: resolveAmlEntityTypeNameFromId(complianceProfile?.amlEntityTypeId),
    entityName: entityName ?? "",
    streetLine1: childAddress?.street ?? "",
    streetLine2: childAddress?.street2 ?? "",
    stateOrProvince: stateOrProvince ?? "",
    country: childAddress?.country?.alpha2code ?? "",
    city: childAddress?.city ?? "",
    zipCode: childAddress?.postalCode ?? "",
    dateOfFormation: dateOfFormation,
    taxId: childProfile?.taxIdUnencrypted ?? "",
    ownershipPercentage: getOwnershipPercentage(complianceProfile?.parentRelationships) ?? 0,
    riskRating: childProfile?.riskRating?.overriddenRating ?? "",
    isOwner: isOwner,
    isController: isController,
    natureOfBusiness: complianceProfile?.natureOfBusiness ?? "",
    principalPlaceOfBusiness: complianceProfile?.principalPlaceOfBusinessId
      ? String(complianceProfile.principalPlaceOfBusinessId)
      : "",
    registrationNumber: complianceProfile?.registrationNumber ?? "",
  };

  const validationSchema = useMemo(
    () => getSchema(needsOwnershipPercentage, ioReusableProfilesEnabled),
    [needsOwnershipPercentage, ioReusableProfilesEnabled],
  );

  /**
   * Overrides the risk rating if the override has been set. This calls a mutation.
   *
   * @param riskRating The risk rating value to override (if blank, function is a noop)
   * @param complianceProfile The profile to override
   */
  async function callOverrideRiskRating(riskRating: string) {
    if (showRiskRating && riskRating && complianceProfile?.id) {
      await overrideRiskRating({
        variables: {
          data: {
            profileId: complianceProfile.id,
            programId: programId,
            overriddenRating: riskRating as RiskRating,
          },
        },
      });
    }
  }

  /**
   * Inserts or updates (depending on the operation) an individual profile. The upsert command
   * here could handle both, but has some wacky behavior for updates so has been replaced with
   * a specific update command leaving the upsert command as just an insert. Whether we insert
   * or update is based on the whether or not complianceProfile has an ID. The value returned
   * is the ID of the new profile.
   *
   * Given this is an edit form, presumably we'll never do an insert, but because the
   * complianceProfile can be undefined, we need to allow for the insertion route.
   *
   * @param data The individual data meant for the upsert command (while be adapted for the
   *             update)
   * @returns The new profile ID and the list of errors (one of these two will always be undefined)
   */
  async function insertOrUpdateInstitutionProfile(
    data: ComplianceInstitutionProfileInput,
  ): Promise<[number | undefined, ReadonlyArray<GraphQLFormattedError> | undefined]> {
    if (complianceProfile?.id) {
      const updatedData = {
        ...data,
        dateOfFormation: data.dateOfFormation ? toISOFormat(data.dateOfFormation) : undefined,
      };

      const response = await updateInstitutionProfile({
        variables: {
          data: updatedData,
          profileId: complianceProfile.id,
          parentProfileId: complianceProfile.parentRelationships[0]?.parentProfileId,
        },
      });

      return [response?.data?.updateInstitutionProfile?.id, response?.errors];
    } else {
      const updatedData = {
        ...data,
        dateOfFormation: data.dateOfFormation ? toISOFormat(data.dateOfFormation) : undefined,
      };

      const response = await createInstitutionProfile({
        variables: {
          arenaAccessId: arenaAccessId ?? 0,
          data: updatedData,
          parentProfileId: complianceProfile?.parentRelationships[0]?.parentProfileId,
        },
      });

      return [response?.data?.createInstitutionProfile?.id, response?.errors];
    }
  }

  async function editExistingInstitution(
    data: ComplianceInstitutionProfileInput,
    riskRating: string,
  ) {
    try {
      await callOverrideRiskRating(riskRating);

      const [childProfileId, errors] = await insertOrUpdateInstitutionProfile(data);

      if (errors || !childProfileId) {
        toast.error("Failed to edit institution profile");
        return;
      }

      if (positionId) {
        await updatePicrPositionDetails({
          variables: {
            data: {
              caseId: caseId,
              positionId: positionId,
              createdProfileId: childProfileId,
            },
          },
        });
      }
      toast.success("Profile updated successfully");
      onCompleted();
    } catch {
      // errors already toasted via onError
    }
  }

  const handleSubmit = async (values: FormValues) => {
    const profileValues = {
      taxId: values.taxId,
      caseId: caseId,
      addresses: [
        {
          addressType: AddressType.Unknown, // We should ask for this?
          street: values.streetLine1,
          street2: values.streetLine2,
          city: values.city,
          state: values.stateOrProvince,
          postalCode: values.zipCode,
          country: values.country,
        },
      ],
    };
    const roles = isBeneficiaryProfile ? undefined : buildRelationshipTypes(values);
    const entityValues = {
      // Not values.accountType — that field means entity vs individual.
      accountType: VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE[values.entityType],
      legalBusinessName: values.entityName,
      dateOfFormation: values.dateOfFormation?.toISOString(),
      taxIdType: TaxIdType.Ein,
      ownershipPercentage: values.ownershipPercentage,
      relationshipType: roles,
      amlEntityId: resolveAmlEntityId(values.amlEntityType, ioReusableProfilesEnabled),
      ...(showOnboardingBusinessFields
        ? {
            natureOfBusiness: values.natureOfBusiness || undefined,
            principalPlaceOfBusinessId: optionalIntField(values.principalPlaceOfBusiness),
            registrationNumber: values.registrationNumber || undefined,
          }
        : {}),
    };

    const data = {
      ...profileValues,
      ...entityValues,
    };

    await editExistingInstitution(data, values.riskRating);
  };

  return (
    <FullscreenTakeover.Body>
      <Form
        submitOnlyChangedValues={false}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Column size="lg">
          <Card className={styles.editEntityCard}>
            <Text marginBottom="md" variant="h3">
              Edit entity
            </Text>
            {needsOwnershipPercentage && !(positionId || isBeneficiaryProfile) && (
              <ProfileEntityFormFields />
            )}
            <EntityFormFields
              isEditMode
              aiExtractionSupported={aiExtractionSupported}
              showOnboardingBusinessFields={showOnboardingBusinessFields}
            />
            {showRiskRating && (
              <RiskRatingRadioBar
                form="entity"
                nonOverrideRiskRating={childProfile?.riskRating?.calculatedRating}
              />
            )}
          </Card>
          <Flex justifyContent="flex-end">
            {onCancel && (
              <CancelButton onClick={onCancel} className={styles.editEntityCancelButton} />
            )}
            <SubmitButton>{saveButtonLabel}</SubmitButton>
          </Flex>
        </Column>
      </Form>
    </FullscreenTakeover.Body>
  );
};

export default EditEntityForm;
